import React from 'react';
import { Layout, Menu, Avatar, Typography, Divider, Badge, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, CloseOutlined } from '@ant-design/icons';
import {
  DashboardOutlined,
  UserOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  MessageOutlined,
  BellOutlined,
  TeamOutlined,
  EyeOutlined,
  StarOutlined,
  SearchOutlined,
  HomeOutlined,
  GlobalOutlined,
  HeartOutlined,
  PhoneOutlined,
  CalendarOutlined,
  BookOutlined,
  ToolOutlined,
  SettingOutlined,
  BarChartOutlined,
  AuditOutlined,
  FlagOutlined,
  UserAddOutlined,
  CrownOutlined,
  BankOutlined,
  PartitionOutlined,
  FolderOutlined,
  ContactsOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  SafetyOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  ShopOutlined,
  TrophyOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons';
import { clearAuth } from '../../../redux/slices/authSlice';

const { Sider } = Layout;

const Sidebar = ({ collapsed, setCollapsed, isMobile }) => {
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = React.useMemo(() => {
    if (!user) return [];

    switch (user.role) {
      case 'ADMIN':
        return [
          { key: 'dashboard', label: 'Dashboard', path: '/admin/dashboard', icon: <DashboardOutlined /> },
          { key: 'users', label: 'User Management', path: '/admin/users', icon: <UserOutlined /> },
          { key: 'areas', label: 'Area Management', path: '/admin/areas', icon: <EnvironmentOutlined /> },
          { key: 'reports', label: 'Reports', path: '/admin/reports', icon: <BarChartOutlined /> },
          { key: 'feedback-ratings', label: 'Feedback & Ratings', path: '/admin/feedback-ratings', icon: <StarOutlined /> },
          { key: 'messages', label: 'Messages', path: '/admin/messages', icon: <MessageOutlined /> },
          { key: 'announcement', label: 'Announcements', path: '/admin/announcement', icon: <BellOutlined /> },
          { key: 'auditLog', label: 'Audit Log', path: '/admin/auditLog', icon: <AuditOutlined /> },
          { key: 'ContentModeration', label: 'Content Moderation', path: '/admin/ContentModeration', icon: <FlagOutlined /> },
          { key: 'VolunteerAssignment', label: 'Volunteer Assignment', path: '/admin/VolunteerAssignment', icon: <UserAddOutlined /> },
          { key: 'VolunteerApplications', label: 'Volunteer Applications', path: '/admin/volunteer-applications', icon: <SafetyOutlined /> },
          { key: 'VolunteerTasks', label: 'Volunteer Tasks', path: '/admin/volunteer-tasks', icon: <ToolOutlined /> },
          { key: 'ProfessionalCollaboration', label: 'Professional Collaboration', path: '/admin/ProfessionalCollaboration', icon: <TeamOutlined /> },
          { key: 'AnalyticsDashboard', label: 'Analytics Dashboard', path: '/admin/AnalyticsDashboard', icon: <BarChartOutlined /> },
          { key: 'Ministry', label: 'Ministry', path: '/admin/Ministry', icon: <BankOutlined /> },
          { key: 'PoliticalParty', label: 'Political Party', path: '/admin/PoliticalParty', icon: <CrownOutlined /> },
          { key: 'PublicOfficials', label: 'Public Officials', path: '/admin/PublicOfficials', icon: <ContactsOutlined /> },
          { key: 'WorkAssignment', label: 'Work Assignment', path: '/admin/work-assignment', icon: <ToolOutlined /> },
          { key: 'AreaUpdateRequests', label: 'Area Update Requests', path: '/admin/area-update-requests', icon: <ApiOutlined /> },
          { key: 'Categories', label: 'Categories', path: '/admin/categories', icon: <FolderOutlined /> },
          { key: 'constituenciesType', label: 'Constituency Types', path: '/admin/constituencies-type', icon: <PartitionOutlined /> },
          { key: 'constituencies', label: 'Constituencies', path: '/admin/constituencies', icon: <GlobalOutlined /> },
        ];
      case 'CITIZEN':
        return [
          { key: 'dashboard', label: 'Dashboard', path: '/citizen/dashboard', icon: <DashboardOutlined /> },
          { key: 'FindYourRepresentative', label: 'Find Representative', path: '/citizen/FindYourRepresentative', icon: <TeamOutlined /> },
          { key: 'FollowedRepresentatives', label: 'My Representatives', path: '/citizen/FollowedRepresentatives', icon: <HeartOutlined /> },
          { key: 'MyIssuesTracker', label: 'My Complaints', path: '/citizen/MyIssuesTracker', icon: <FileTextOutlined /> },
          { key: 'VolunteerApplication', label: 'Become Volunteer', path: '/citizen/volunteer-application', icon: <SafetyOutlined /> },
          { key: 'messages', label: 'Messages', path: '/citizen/messages', icon: <MessageOutlined /> },
          { key: 'CollaborationRequests', label: 'Collaboration Requests', path: '/citizen/CollaborationRequests', icon: <ThunderboltOutlined /> },
          { key: 'FeedbackRating', label: 'Feedback & Rating', path: '/citizen/FeedbackRating', icon: <StarOutlined /> },
          // { key: 'LocalServicesDirectory', label: 'Local Services', path: '/citizen/LocalServicesDirectory', icon: <ShopOutlined /> },
          { key: 'civic-education', label: 'Civic Education', path: '/citizen/civic-education', icon: <BookOutlined /> },
        ];
      case 'VOLUNTEER':
        return [
          { key: 'dashboard', label: 'Dashboard', path: '/volunteer/dashboard', icon: <DashboardOutlined /> },
          { key: 'FindYourRepresentative', label: 'Find Representative', path: '/volunteer/FindYourRepresentative', icon: <TeamOutlined /> },
          { key: 'FollowedRepresentatives', label: 'My Representatives', path: '/volunteer/FollowedRepresentatives', icon: <HeartOutlined /> },
          { key: 'MyIssuesTracker', label: 'My Complaints', path: '/volunteer/MyIssuesTracker', icon: <FileTextOutlined /> },
          { key: 'messages', label: 'Messages', path: '/volunteer/messages', icon: <MessageOutlined /> },
          { key: 'CollaborationRequests', label: 'Collaboration Requests', path: '/volunteer/CollaborationRequests', icon: <ThunderboltOutlined /> },
          { key: 'FeedbackRating', label: 'Feedback & Rating', path: '/volunteer/FeedbackRating', icon: <StarOutlined /> },
          { key: 'civic-education', label: 'Civic Education', path: '/volunteer/civic-education', icon: <BookOutlined /> },
          { key: 'area-updates', label: 'Area Update Requests', path: '/volunteer/area-update-requests', icon: <ToolOutlined /> },
          { key: 'my-volunteer-tasks', label: 'My Tasks', path: '/volunteer/my-tasks', icon: <FileSearchOutlined /> },
        ];
      case 'REPRESENTATIVE':
        return [
          { key: 'dashboard', label: 'Dashboard', path: '/representative/dashboard', icon: <DashboardOutlined /> },
          { key: 'complaints', label: 'Complaint Management', path: '/representative/complaints', icon: <FileTextOutlined /> },
          { key: 'announcements', label: 'Announcements', path: '/representative/announcements', icon: <BellOutlined /> },
          { key: 'messages', label: 'Messages', path: '/representative/messages', icon: <MessageOutlined /> },
          { key: 'CalendarView', label: 'Calendar View', path: '/representative/CalendarView', icon: <CalendarOutlined /> },
          { key: 'FieldVisitNotes', label: 'Field Visit Notes', path: '/representative/FieldVisitNotes', icon: <BookOutlined /> },
          { key: 'VolunteerCoordination', label: 'Volunteer Coordination', path: '/representative/VolunteerCoordination', icon: <UserAddOutlined /> },
          { key: 'IssueManagement', label: 'Issue Management', path: '/representative/IssueManagement', icon: <ToolOutlined /> },
          { key: 'profile', label: 'Profile Management', path: '/representative/profile', icon: <SettingOutlined /> },
        ];
      default:
        return [];
    }
  }, [user]);

  const selectedKey = menuItems.find(item => location.pathname.startsWith(item.path))?.key;

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN': return '#ff4d4f';
      case 'CITIZEN': return '#1890ff';
      case 'VOLUNTEER': return '#13c2c2';
      case 'REPRESENTATIVE': return '#52c41a';
      default: return '#666';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'ADMIN': return <CrownOutlined />;
      case 'CITIZEN': return <UserOutlined />;
      case 'VOLUNTEER': return <SafetyOutlined />;
      case 'REPRESENTATIVE': return <TeamOutlined />;
      default: return <UserOutlined />;
    }
  };

  const handleLogout = () => {
    dispatch(clearAuth());
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <>
      <style>
        {`
          .sidebar-menu-scroll::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <Sider 
        collapsible 
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={280}
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        style={{
          background: 'linear-gradient(180deg, #001529 0%, #002140 100%)',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          overflow: 'hidden',
          height: '100vh',
          zIndex: 1200,
          pointerEvents: isMobile && collapsed ? 'none' : 'auto'
        }}
        theme="dark"
      >
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none'
      }} />

      {isMobile && !collapsed && (
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setCollapsed(true)}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 11,
            color: 'white',
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 6
          }}
        />
      )}
      
      {/* Toggle Button */}
      {!isMobile && (
        <div style={{
          position: 'absolute',
          top: '24px',
          right: '16px',
          zIndex: 10
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              color: 'rgba(255, 255, 255, 0.7)',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              e.target.style.color = 'rgba(255, 255, 255, 0.7)';
            }}
          />
        </div>
      )}
      
      {/* Brand Section */}
      <div style={{
        padding: isMobile ? '54px 20px 18px' : (collapsed ? '24px 12px' : '24px 20px'),
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: collapsed ? '0' : '8px',
          justifyContent: collapsed ? 'center' : 'flex-start'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${getRoleColor(user?.role)} 0%, ${getRoleColor(user?.role)}CC 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            color: 'white',
            boxShadow: `0 4px 12px ${getRoleColor(user?.role)}40`
          }}>
            {getRoleIcon(user?.role)}
          </div>
          {!collapsed && (
            <div>
              <Typography.Title 
                level={4} 
                style={{ 
                  color: 'white', 
                  margin: 0, 
                  fontSize: '18px',
                  fontWeight: '600'
                }}
              >
                Uttardayitav
              </Typography.Title>
              <Typography.Text 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  fontSize: '12px',
                  textTransform: 'capitalize'
                }}
              >
                {user?.role?.toLowerCase()} Portal
              </Typography.Text>
            </div>
          )}
        </div>
        
        {/* User Info - Only show when not collapsed */}
        {!collapsed && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            marginTop: '12px'
          }}>
            <Avatar 
              src={user?.profileImage} 
              icon={<UserOutlined />}
              size="small"
              style={{ 
                background: getRoleColor(user?.role),
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text 
                style={{ 
                  color: 'white', 
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user?.name || 'User'}
              </Typography.Text>
              <Typography.Text 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.6)', 
                  fontSize: '11px',
                  display: 'block'
                }}
              >
                {user?.email || 'user@example.com'}
              </Typography.Text>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu - Scrollable */}
      <div 
        className="sidebar-menu-scroll"
        style={{ 
          position: 'absolute',
          top: '140px', // Start after brand section
          bottom: '80px', // Space for logout button
          left: 0,
          right: 0,
          overflowY: 'auto',
          padding: '0 0 16px 0',
          zIndex: 1,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // IE and Edge
        }}>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        onClick={({ key }) => {
          const item = menuItems.find(i => i.key === key);
          if (item) {
            navigate(item.path);
            if (isMobile) {
              setCollapsed(true);
            }
          }
        }}
        items={menuItems.map(item => ({
          key: item.key,
          label: item.label,
            icon: item.icon,
            style: {
              margin: '2px 8px',
              borderRadius: '8px',
              height: '44px',
              lineHeight: '44px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              border: '1px solid transparent'
            }
          }))}
          style={{
            background: 'transparent',
            border: 'none'
          }}
        />
      </div>

      {/* Fixed Logout Button */}
      {(!isMobile || !collapsed) && (
        <div style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          width: isMobile ? '280px' : (collapsed ? '80px' : '280px'),
          background: '#001529',
          padding: collapsed && !isMobile ? '12px 12px' : '12px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 1000,
          transition: 'width 0.2s ease'
        }}>
          <Button
            type="text"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            title={collapsed && !isMobile ? 'Logout' : ''}
            style={{
              width: '100%',
              height: '36px',
              borderRadius: '6px',
              background: 'rgba(255, 77, 79, 0.1)',
              border: '1px solid rgba(255, 77, 79, 0.3)',
              color: '#ff4d4f',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: collapsed && !isMobile ? '0' : '6px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 77, 79, 0.2)';
              e.target.style.borderColor = 'rgba(255, 77, 79, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 77, 79, 0.1)';
              e.target.style.borderColor = 'rgba(255, 77, 79, 0.3)';
            }}
          >
            {collapsed && !isMobile ? '' : 'Logout'}
          </Button>
        </div>
      )}
    </Sider>
    </>
  );
};

export default Sidebar;
